import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Box, Stack, Typography } from '@mui/material'

import { DiscontinuedSkullIcon } from '../../components/DiscontinuedSkullIcon/DiscontinuedSkullIcon'
import { GRTooltip } from '../../components/GRTooltip/GRTooltip'
import { useCurrentUserLanguage } from '../../features/account/hooks/useCurrentUserLanguage'
import { BlueprintGamesDrilldownTable } from '../../features/market-analytics/components/BlueprintGamesDrilldownTable/BlueprintGamesDrilldownTable'
import {
  BlueprintHierarchyBar,
  BlueprintHierarchyLinks,
  BlueprintHierarchyMenus,
} from '../../features/market-analytics/components/BlueprintHierarchyBar/BlueprintHierarchyBar'
import { BlueprintScreenshotsCard } from '../../features/market-analytics/components/BlueprintScreenshotsCard/BlueprintScreenshotsCard'
import { DetailPageMessageCard } from '../../features/market-analytics/components/DetailPageMessageCard/DetailPageMessageCard'
import { GlossaryDescriptionCard } from '../../features/market-analytics/components/GlossaryDescriptionCard/GlossaryDescriptionCard'
import { LevelStatsCard, densityStatsOf } from '../../features/market-analytics/components/LevelStatsCard/LevelStatsCard'
import { LevelTrendsChart } from '../../features/market-analytics/components/LevelTrendsChart/LevelTrendsChart'
import { MarketAnalyticsFilterBar } from '../../features/market-analytics/components/MarketAnalyticsFilterBar'
import { NewAdoptersModal } from '../../features/market-analytics/components/NewAdoptersModal'
import { TypicalDurationsCard } from '../../features/market-analytics/components/TypicalDurationsCard/TypicalDurationsCard'
import {
  adoptingGamesOf,
  buildEventCategoryPath,
  buildEventTypePath,
  resolveCohortSize,
  familyEventTypeMemberships,
  filterCatalogueToScope,
  buildBlueprintFamilyPath,
} from '../../features/market-analytics/components/helpers/catalogueUtils'
import { toDurationBuckets } from '../../features/market-analytics/components/helpers/durationBuckets'
import { gameIconUrlOf } from '../../features/market-analytics/components/helpers/gameBlueprintDisplay'
import { buildHierarchySiblings } from '../../features/market-analytics/components/helpers/hierarchySiblings'
import { getTranslatedName } from '../../features/market-analytics/components/helpers/metricUtils'
import { toNewAdopterGames } from '../../features/market-analytics/components/helpers/newAdopterGames'
import { selectedPeriodFor } from '../../features/market-analytics/components/helpers/trendsSelectedPeriod'
import { useBlueprintDetailData } from '../../features/market-analytics/hooks/useBlueprintDetailData'
import { useCatalogueScope, useCatalogueTaxonomyCounts } from '../../features/market-analytics/hooks/useCatalogueScope'
import { useNewAdopterGameIcons } from '../../features/market-analytics/hooks/useNewAdopterGameIcons'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import usePage from '../../hooks/usePage'
import languageService from '../../services/LanguageService'
import PageService from '../../services/PageService'
import './MarketLevelBlueprintDetailPage.scss'
import { MarketLevelBlueprintDetailPageSkeleton } from './MarketLevelBlueprintDetailPageSkeleton'

/**
 * Market-level blueprint detail page rebuilt to the v14 mockup: breadcrumb +
 * header, stacked description + screenshots cards, segment filter bar, adoption stats,
 * typical durations (backend-blocked, hidden) and the 3-level games drill-down.
 * The former key-metrics row, line-chart trends, seasonality, implementation
 * insights, top-games and related-blueprints sections are parked (components
 * remain in the repo) per the v14 restructure.
 */
const MarketLevelBlueprintDetailPageContent: React.FC = () => {
  const { t } = useTranslation()
  const { blueprintId } = useParams<{ blueprintId: string }>()
  const userLanguage = useCurrentUserLanguage()

  const skip = !blueprintId

  // Same URL-backed state as the catalogue (`category`/`genre`/`subgenre`/`win`
  // params), so filters carried over from the catalogue CTA parse natively and
  // links from this page are shareable.
  //
  // Arriving via the catalogue's "Open full details" CTA attaches the
  // catalogue's *full* search string (event filters, granularity, view, sort,
  // …) as router navigation state — richer than the taxonomy+window subset the
  // URL can express. The breadcrumb's back link prefers it so the user returns
  // to the exact catalogue view they left. The momentum snapshot it may carry
  // describes this blueprint only, so links to other hierarchy pages forward
  // just the back search (`hierarchyNavState`).
  const {
    state,
    tagGroupInfos,
    windowDays,
    selectedCategoryIds,
    selectedGenreIds,
    selectedSubgenreIds,
    effectiveSubgenreIds,
    scope,
    isUnscoped,
    carriedSearch,
    navState,
    rootCrumbSearch: breadcrumbSearch,
    crumbNavState: hierarchyNavState,
  } = useCatalogueScope()
  // Both halves are required before the snapshot can stand in for the momentum request:
  // the count is not a share without the cohort it was counted over, so accepting a
  // snapshot that carries only `deltaGames` skips the query AND has no denominator to
  // divide by, leaving the tile showing a raw game count where the catalogue it was
  // clicked from showed a percentage.
  const hasNavDelta = navState?.deltaWindowDays === windowDays && navState?.deltaGames !== undefined && navState?.cohortSize !== undefined

  // ---- Data ------------------------------------------------------------------
  const {
    blueprint,
    currentBlueprint,
    isBlueprintLoading,
    isBlueprintFetching,
    isBlueprintError,
    isBlueprintNotFound,
    isFamilyGlossaryPending,
    catalogueList,
    currentCatalogueList,
    isCatalogueListFetching,
    groupRollup,
    isRollupFetching,
    momentumData,
    isMomentumFetching,
    trends,
    isTrendsFetching,
    isTrendsError,
    refetchTrends,
    gameBlueprints,
    isGameBlueprintsLoading,
    isGameBlueprintsError,
    refetchGameBlueprints,
    pcConsoleBlueprints,
    durationDistribution,
    isDurationFetching,
    retryFailedRequests,
  } = useBlueprintDetailData(blueprintId, { windowDays, scope, skipMomentum: hasNavDelta })

  // Taxonomy picker counts, from the unscoped snapshot as on the catalogue page.
  const { taxonomyCounts } = useCatalogueTaxonomyCounts(catalogueList, isUnscoped, { skip })

  // Adopting games for the Adoption tile, taken from the list endpoint — the
  // same row the catalogue's ranked list reads, so the tile and the tree agree.
  //
  // Falls back to the detail endpoint's figure when the blueprint has no list
  // row: the scoped list only carries families with events inside the window.
  // Both endpoints count the same thing (cohort games with an event during the
  // period's last month), so the fallback is on the same scale as the tile's denominator.
  const listRow = useMemo(() => (currentCatalogueList ?? []).find((bp) => bp.id === blueprintId), [currentCatalogueList, blueprintId])
  const adoptingGames = listRow ? adoptingGamesOf(listRow) : currentBlueprint?.distinctGamesCount ?? null
  // Whole-market rate shown when the cohort size is unknown. Same row as the
  // count above, so the tile never mixes the two endpoints' figures.
  const adoptionRateFallback = (listRow ?? currentBlueprint)?.adoptionRate?.windowed
  // Games whose FIRST event of this family fell inside the period, from whichever of
  // the two endpoints answered — both report the same `newGames`. The backend
  // names them now, so the tile no longer has to derive the list from the drilldown.
  const newGames = (listRow ?? currentBlueprint)?.newGames

  // The list/detail row states the cohort for the same request, so prefer it and keep
  // the rollup as the fallback (and the catalogue's shared cache entry).
  const cohortSize = useMemo(
    () => (listRow ?? currentBlueprint)?.cohortGamesCount ?? resolveCohortSize(groupRollup ?? []),
    [listRow, currentBlueprint, groupRollup]
  )

  // The momentum count and the cohort it was counted over travel together — the count
  // is not a share without its own denominator (see `adoptionChangePp`). The cohort is
  // the same one the Adoption tile divides by now, so it is only read here as a
  // fallback for environments whose list/rollup predate `cohortGamesCount`.
  const momentum = useMemo(() => {
    if (hasNavDelta) return { deltaGames: navState?.deltaGames ?? null, cohortGamesCount: navState?.cohortSize ?? null }
    const row = (momentumData ?? []).find((item) => item.id === blueprintId)
    return { deltaGames: row?.momentum ?? null, cohortGamesCount: row?.cohortGamesCount ?? null }
  }, [hasNavDelta, navState?.deltaGames, navState?.cohortSize, momentumData, blueprintId])
  const deltaGames = momentum.deltaGames

  // The months the chart plots: the selected period's, which is what the trends
  // response covers. The stat tiles read the same response.
  const selectedPeriod = useMemo(() => selectedPeriodFor(windowDays), [windowDays])

  // Fallback icons for New Adopters entries that arrive without their own (backends
  // predating GR-3245), joined in from the drilldown payload this page already fetches;
  // a game missing from both falls back to a letter avatar rather than dropping out.
  const iconByGameId = useMemo(() => {
    const byId: Record<string, string> = {}
    for (const item of gameBlueprints ?? []) {
      const gameId = item.gameId ?? item.id
      const icon = gameIconUrlOf(item)
      if (icon && !byId[gameId]) byId[gameId] = icon
    }
    return byId
  }, [gameBlueprints])

  // Publishers for the same games, shown in the unlock dialog of a locked New Adopter (the
  // backend's `newGames` doesn't send one).
  const artistByGameId = useMemo(() => {
    const byId: Record<string, string> = {}
    for (const item of gameBlueprints ?? []) {
      const gameId = item.gameId ?? item.id
      if (item.artist && !byId[gameId]) byId[gameId] = item.artist
    }
    return byId
  }, [gameBlueprints])

  // Platform (mobile vs PC/console) of the same games, for the badge on their icons. The `/blueprints`
  // payload doesn't carry it, so it comes from the tracked-games list, which covers both kinds and is the
  // cached request the adopters table below already makes.
  const { appTypeByGameId, platformsByGameId } = useNewAdopterGameIcons()

  // The games behind the New Adopters tile, named by the backend. Held back while the
  // icons are still loading so the faces don't pop in one render later. The same
  // payload names the blueprint each game adopted with, for the modal's event links.
  const newAdopterGames = useMemo(
    () =>
      isGameBlueprintsLoading
        ? undefined
        : toNewAdopterGames(newGames, { userLanguage, iconByGameId, artistByGameId, appTypeByGameId, platformsByGameId, gameBlueprints }),
    [isGameBlueprintsLoading, newGames, userLanguage, iconByGameId, artistByGameId, appTypeByGameId, platformsByGameId, gameBlueprints]
  )
  const [isNewAdoptersModalOpen, setIsNewAdoptersModalOpen] = useState(false)
  const openNewAdoptersModal = useCallback(() => setIsNewAdoptersModalOpen(true), [])
  const closeNewAdoptersModal = useCallback(() => setIsNewAdoptersModalOpen(false), [])

  const durationBuckets = useMemo(() => toDurationBuckets(durationDistribution, t), [durationDistribution, t])

  // Density tiles read the per-adopting-game measure of the same trends response
  // the chart plots, so the tile and the line can't disagree.
  const { densityScalar } = useMemo(() => densityStatsOf(trends?.density), [trends?.density])

  // ---- Derived display values ---------------------------------------------------
  const blueprintName = blueprint ? getTranslatedName(blueprint.names, userLanguage) : ''
  // Stable identity: LevelTrendsChart feeds a memoized chart card, so an inline object here
  // would rebuild the chart on every page render.
  const blueprintIdForTrends = blueprint?.id
  const trendsTotal = useMemo(
    () => (blueprintIdForTrends ? { item: { id: blueprintIdForTrends, name: blueprintName }, source: trends, period: selectedPeriod ?? undefined } : null),
    [blueprintIdForTrends, blueprintName, trends, selectedPeriod]
  )
  const description = blueprint?.descriptions ? getTranslatedName(blueprint.descriptions, userLanguage) : ''
  // Every event type the family belongs to, leading (most windowed games) first.
  // With more than one, the event type crumb reads "Multiple event types" and its
  // menu lists them; the category crumb does the same when the types span categories.
  // Empty when the family ran nothing in the window, which leaves the crumbs blank
  // rather than filing it under a type it did not actually run — the same placement
  // the catalogue filters use, so the crumbs and the filters never disagree.
  const memberships = useMemo(() => (blueprint ? familyEventTypeMemberships(blueprint) : []), [blueprint])
  // Single-value crumbs fall back to the leading membership.
  const tagGroupId = memberships[0]?.categoryId ?? ''
  const tagId = memberships[0]?.typeId ?? ''
  const hasMultipleTypes = memberships.length > 1
  const membershipCategoryIds = useMemo(() => Array.from(new Set(memberships.map((m) => m.categoryId).filter(Boolean))), [memberships])
  const hasMultipleCategories = membershipCategoryIds.length > 1
  const categoryName = hasMultipleCategories
    ? t('market-analytics:hierarchy_multiple_event_categories')
    : tagGroupId
    ? languageService.getTranslation('tagGroups', tagGroupId) || tagGroupId
    : ''
  const eventTypeName = hasMultipleTypes
    ? t('market-analytics:hierarchy_multiple_event_types')
    : tagId
    ? languageService.getTranslation('tags', tagId) || tagId
    : ''

  useDocumentTitle(t('sidebar:market_analytics') + (blueprintName ? `: ${blueprintName}` : ''))

  // The hierarchy menus' inventory tracks the segment filter the same way the
  // catalogue list does (client-side membership filter), but the currently
  // viewed blueprint is always kept so the menus never lose their anchor.
  const scopedCatalogueList = useMemo(
    () => filterCatalogueToScope(catalogueList, effectiveSubgenreIds, blueprintId),
    [catalogueList, effectiveSubgenreIds, blueprintId]
  )

  // Sibling switchers in the hierarchy bar, over the segment-scoped inventory so
  // they list exactly what the current filters admit. Every level jumps straight
  // to its own page (category, event type, blueprint).
  const hierarchyMenus = useMemo<BlueprintHierarchyMenus>(() => {
    const siblings = buildHierarchySiblings(scopedCatalogueList, {
      categoryId: tagGroupId,
      eventTypeId: tagId,
      userLanguage,
      tagGroups: tagGroupInfos,
    })

    // For a family spanning several event types the crumb menus list the
    // family's own types / categories (each opening its page) instead of the
    // most-used type's siblings; none of them is "current".
    const eventCategoryOptions = hasMultipleCategories
      ? siblings.eventCategories.filter((sibling) => membershipCategoryIds.includes(sibling.id))
      : siblings.eventCategories
    const membershipTypeIds = new Set(memberships.map((m) => m.typeId))
    const eventTypeOptions = hasMultipleTypes
      ? buildHierarchySiblings(scopedCatalogueList, { userLanguage, tagGroups: tagGroupInfos }).eventTypes.filter((sibling) =>
          membershipTypeIds.has(sibling.id)
        )
      : siblings.eventTypes

    return {
      eventCategory: {
        ariaLabel: t('market-analytics:hierarchy_menu_event_categories'),
        options: eventCategoryOptions.map((sibling) => ({
          key: sibling.id,
          label: sibling.label,
          isCurrent: !hasMultipleCategories && sibling.id === tagGroupId,
          to: buildEventCategoryPath(sibling.id, carriedSearch),
          state: hierarchyNavState,
          colorHex: sibling.colorHex,
        })),
      },
      eventType: {
        ariaLabel: t('market-analytics:hierarchy_menu_event_types'),
        options: eventTypeOptions.map((sibling) => ({
          key: sibling.id,
          label: sibling.label,
          isCurrent: !hasMultipleTypes && sibling.id === tagId,
          to: buildEventTypePath(sibling.id, carriedSearch),
          state: hierarchyNavState,
          colorHex: sibling.colorHex,
        })),
      },
      blueprint: {
        ariaLabel: t('market-analytics:hierarchy_menu_blueprints'),
        options: siblings.blueprints.map((sibling) => ({
          key: sibling.id,
          label: sibling.label,
          isCurrent: sibling.id === blueprintId,
          to: buildBlueprintFamilyPath(sibling.id, carriedSearch),
          state: hierarchyNavState,
          colorHex: sibling.colorHex,
        })),
      },
    }
  }, [
    scopedCatalogueList,
    tagGroupId,
    tagId,
    blueprintId,
    carriedSearch,
    hierarchyNavState,
    userLanguage,
    tagGroupInfos,
    t,
    memberships,
    hasMultipleTypes,
    hasMultipleCategories,
    membershipCategoryIds,
  ])

  // The category and event-type crumbs open their own pages; their chevrons keep
  // the sibling menus above. A "Multiple …" crumb has no single page to open, so
  // it stays a label and the menu does the navigating.
  const hierarchyLinks = useMemo<BlueprintHierarchyLinks>(
    () => ({
      eventCategory: tagGroupId && !hasMultipleCategories ? buildEventCategoryPath(tagGroupId, carriedSearch) : undefined,
      eventType: tagId && !hasMultipleTypes ? buildEventTypePath(tagId, carriedSearch) : undefined,
    }),
    [tagGroupId, tagId, carriedSearch, hasMultipleTypes, hasMultipleCategories]
  )

  // Only block the page render on the small/fast blueprint metadata query while
  // this family has none yet — filter/window changes refetch in place with
  // per-section loading. The selection counts let the skeleton's filter-bar
  // placeholder reserve the same chip rows the resolved bar will render for the
  // carried-over filters.
  // A failed fetch stays on the skeleton until the glossary has said whether the
  // family exists, so the retry card doesn't flash before the not-found card.
  if (isBlueprintLoading || (isBlueprintFetching && !blueprint) || isFamilyGlossaryPending) {
    return (
      <MarketLevelBlueprintDetailPageSkeleton
        selectedCategoryCount={selectedCategoryIds.length}
        selectedGenreCount={selectedGenreIds.length}
        selectedSubgenreCount={selectedSubgenreIds.length}
      />
    )
  }

  // A failed fetch or an unknown blueprint id renders a retry / not-found card
  // with the back navigation kept intact. `blueprint` still holds the previous
  // segment's family after a failed refetch, so the error is checked first:
  // otherwise that segment's figures would stay on screen as the current ones.
  if (isBlueprintError || !blueprint) {
    const isRetryable = isBlueprintError && !isBlueprintNotFound
    return (
      <DetailPageMessageCard
        className="MarketLevelBlueprintDetailPage"
        title={isRetryable ? t('market-analytics:blueprint_load_error') : t('market-analytics:blueprint_not_found')}
        description={
          isRetryable ? t('market-analytics:blueprint_load_error_description') : t('market-analytics:blueprint_not_found_description', { id: blueprintId })
        }
        backTo={`/live-event-analytics${breadcrumbSearch}`}
        onRetry={isRetryable ? retryFailedRequests : undefined}
      />
    )
  }

  return (
    <Box className="MarketLevelBlueprintDetailPage">
      {/* Hierarchy bar: back to catalog + category › type › blueprint trail */}
      <BlueprintHierarchyBar
        categoryName={categoryName}
        eventTypeName={eventTypeName}
        blueprintName={blueprintName}
        catalogueSearch={breadcrumbSearch}
        menus={hierarchyMenus}
        links={hierarchyLinks}
        linkState={hierarchyNavState}
      />

      {/* Segment filter: same cascading Category / Genre / Subgenre chips as the
          catalogue, and in the same spot (directly under the hierarchy bar) so a
          selection carried over from the catalogue is where the user left it
          instead of buried between the page's content cards. */}
      <Box sx={{ mt: 1.5 }}>
        <MarketAnalyticsFilterBar
          selectedCategoryIds={selectedCategoryIds}
          selectedGenreIds={selectedGenreIds}
          selectedSubgenreIds={selectedSubgenreIds}
          onSetCategories={state.setCategories}
          onSetGenres={state.setGenres}
          onSetSubgenres={state.setSubgenres}
          onClearCategories={state.clearCategories}
          onClearGenres={state.clearGenres}
          onClearSubgenres={state.clearSubgenres}
          onClearTaxonomy={state.clearTaxonomy}
          taxonomyCounts={taxonomyCounts}
          cohortGamesCount={cohortSize}
        />
      </Box>

      {/* Header: blueprint title. The category › type kicker was dropped — the
          sticky hierarchy bar above already shows the same trail. */}
      <Box sx={{ mb: 2.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h2" component="h1" sx={{ fontWeight: 700, lineHeight: 1.2, wordBreak: 'break-word' }}>
            {blueprintName}
          </Typography>
          {blueprint.discontinued ? (
            <GRTooltip content={t('market-analytics:discontinued_blueprint_tooltip')} styles={{ display: 'inline-flex', cursor: 'help', flexShrink: 0 }}>
              <DiscontinuedSkullIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
            </GRTooltip>
          ) : null}
        </Stack>
      </Box>

      {/* Single full-width column. The sticky sibling rail that used to sit on
          the right was dropped — the hierarchy bar's sibling dropdowns cover the
          same navigation without spending a third of the page on it. */}
      <Stack spacing={2} sx={{ minWidth: 0 }}>
        {/* Description + example screenshots stacked full width — the two-up
            grid squeezed the screenshots into a half column and made them
            unreadably small (analyst feedback on the v14 staging build). */}
        <GlossaryDescriptionCard kind="blueprint" description={description} isLoading={isBlueprintFetching && !description} />
        <BlueprintScreenshotsCard blueprintId={blueprint.id} />

        {/* Same card as the event type and category pages: four tiles and the
            family's own trend line, metric lens as tabs on the chart. */}
        <LevelStatsCard
          kind="blueprint"
          window={state.window}
          onWindowChange={state.setWindow}
          adoptingGames={adoptingGames}
          cohortSize={cohortSize ?? momentum.cohortGamesCount}
          adoptionRateFallback={adoptionRateFallback}
          deltaGames={deltaGames}
          densityScalar={densityScalar}
          newAdopters={newGames?.length ?? null}
          newAdopterGames={newAdopterGames}
          onNewAdoptersClick={openNewAdoptersModal}
          isAdoptionLoading={isBlueprintFetching || isCatalogueListFetching || isRollupFetching}
          isNewAdoptersLoading={isGameBlueprintsLoading || (isBlueprintFetching && !listRow)}
          isChangeLoading={isMomentumFetching}
          isDensityLoading={isTrendsFetching}
          chart={
            <LevelTrendsChart
              metric={state.listSort}
              onMetricChange={state.setListSort}
              nameColumnHeader={t('market-analytics:market_level_blueprint_name')}
              total={trendsTotal}
              isTotalLoading={isTrendsFetching && !trends}
              isTotalError={isTrendsError && !trends}
              onTotalRetry={refetchTrends}
              cohortSize={cohortSize}
              embedded
            />
          }
        />

        {/* Typical durations histogram (GR-2916 duration-distribution endpoint);
            hidden when every bucket is empty, skeleton while the fetch resolves. */}
        <TypicalDurationsCard buckets={durationBuckets} isLoading={isDurationFetching && !durationDistribution} />

        <BlueprintGamesDrilldownTable
          blueprintId={blueprint.id}
          blueprintName={blueprintName}
          eventTypeId={tagId}
          gameBlueprints={gameBlueprints}
          effectiveSubgenreIds={effectiveSubgenreIds}
          isLoading={isGameBlueprintsLoading}
          isError={isGameBlueprintsError}
          onRetry={refetchGameBlueprints}
          pcConsoleBlueprints={pcConsoleBlueprints}
        />
      </Stack>

      <NewAdoptersModal
        open={isNewAdoptersModalOpen}
        onClose={closeNewAdoptersModal}
        games={newAdopterGames ?? []}
        isLoading={isGameBlueprintsLoading || (isBlueprintFetching && !listRow)}
        entityName={blueprintName}
        blueprintFamilyId={blueprint.id}
        eventTypeId={tagId}
        window={state.window}
      />
    </Box>
  )
}

/**
 * Moving to a sibling family (hierarchy-bar menus) keeps the same route, so the
 * page is keyed by the id: every bit of per-family UI state — the drill-down's
 * year filter, sort and open rows, the screenshot lightbox, the New Adopters
 * modal — starts fresh instead of carrying over to the next family.
 */
const MarketLevelBlueprintDetailPage: React.FC = () => {
  const { blueprintId } = useParams<{ blueprintId: string }>()

  usePage(PageService.getPageWithId('market-analytics-detail'), 'Visited Market Analytics Blueprint Detail')

  return <MarketLevelBlueprintDetailPageContent key={blueprintId} />
}

export default MarketLevelBlueprintDetailPage
